import React, { useEffect, useRef, useState } from 'react';

const META_PIXEL_ID = ''; // paste your Pixel ID here

export const STORAGE_KEY = 'tm-cookie-consent';

/**
 * Tell AdSense whether it may personalise.
 *
 * The tag itself ships in index.html and loads for everyone; this is the only
 * thing consent controls. Declining must still produce an ad request, just a
 * non-personalised one, otherwise a refusal costs the pageview entirely.
 *
 * Called from main.jsx before render so the flag is queued ahead of the first
 * slot request. A change made through the banner applies to the next
 * navigation: slots already requested keep the setting they were served with.
 */
export function setAdsPersonalisation(allowed) {
  try {
    const ads = (window.adsbygoogle = window.adsbygoogle || []);
    ads.requestNonPersonalizedAds = allowed ? 0 : 1;
  } catch { /* ignore — the tag may be blocked client-side */ }
}

const CATEGORIES = [
  {
    id: 'functional',
    label: 'Functional',
    description: 'Essential for the site to work. Cannot be disabled.',
    required: true,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    // Paste your Google Analytics / Plausible tracking ID here when ready:
    // GA4 example: replace REPLACE_WITH_GA4_ID with "G-XXXXXXXXXX"
    description: 'Helps us understand which episodes people find interesting.',
    required: false,
  },
  {
    id: 'advertising',
    label: 'Advertising',
    // Controls ad personalisation, not whether ads appear. Meta Pixel example:
    // replace REPLACE_WITH_META_PIXEL_ID with your 16-digit pixel ID.
    description: 'Used to show relevant ads and measure campaign performance.',
    required: false,
  },
];

// S-004: validate consent object shape before trusting it
function isValidConsent(obj) {
  return obj && typeof obj === 'object' &&
    typeof obj.functional === 'boolean' &&
    typeof obj.analytics === 'boolean' &&
    typeof obj.advertising === 'boolean';
}

function loadConsent() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // S-004: if invalid, treat as no consent (show banner again)
      if (isValidConsent(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  return null;
}

function saveConsent(prefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
}

/**
 * Fire consent-gated scripts based on stored preferences.
 * Add real script initialisation here once you have the IDs.
 */
function applyConsent(prefs) {
  if (prefs.analytics) {
    // TODO: initialise Google Analytics / Plausible here
    // Example (GA4):
    //   const s = document.createElement('script');
    //   s.src = `https://www.googletagmanager.com/gtag/js?id=REPLACE_WITH_GA4_ID`;
    //   s.async = true;
    //   document.head.appendChild(s);
    //   window.dataLayer = window.dataLayer || [];
    //   window.gtag = function(){ dataLayer.push(arguments); };
    //   gtag('js', new Date());
    //   gtag('config', 'REPLACE_WITH_GA4_ID');
  }
  setAdsPersonalisation(prefs.advertising);
  if (prefs.advertising && META_PIXEL_ID) {
    // Meta Pixel base code
    // eslint-disable-next-line no-unused-expressions
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }
}

// S-005: expose a way to re-open cookie preferences from outside the component
export function openCookieSettings() {
  window.__tmOpenCookieSettings?.();
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({
    functional: true,
    analytics: false,
    advertising: false,
  });
  // FE-010: ref for focus management
  const firstBtnRef = useRef(null);
  const managePanelRef = useRef(null);

  useEffect(() => {
    const stored = loadConsent();
    if (stored) {
      applyConsent(stored);
    } else {
      setVisible(true);
    }
    // S-005: register global opener
    window.__tmOpenCookieSettings = () => setVisible(true);
    return () => { delete window.__tmOpenCookieSettings; };
  }, []);

  // FE-010: move focus to first button when banner becomes visible
  useEffect(() => {
    if (visible) firstBtnRef.current?.focus();
  }, [visible]);

  // NEW-002: move focus to first interactive element in manage panel when it opens
  useEffect(() => {
    if (managing && managePanelRef.current) {
      const firstInteractive = managePanelRef.current.querySelector('button, input, [tabindex]');
      firstInteractive?.focus();
    }
  }, [managing]);

  function acceptAll() {
    const all = { functional: true, analytics: true, advertising: true };
    saveConsent(all);
    applyConsent(all);
    setVisible(false);
  }

  function rejectAll() {
    const minimal = { functional: true, analytics: false, advertising: false };
    saveConsent(minimal);
    applyConsent(minimal);
    setVisible(false);
  }

  function saveManaged() {
    const chosen = { ...prefs, functional: true };
    saveConsent(chosen);
    applyConsent(chosen);
    setVisible(false);
    setManaging(false);
  }

  function togglePref(id) {
    setPrefs(p => ({ ...p, [id]: !p[id] }));
  }

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-modal="false" aria-label="Cookie consent">
      <div className="cookie-banner__inner">
        {!managing ? (
          <>
            <div className="cookie-banner__text">
              <p className="cookie-banner__heading">You're in control</p>
              <p className="cookie-banner__body">
                We use cookies to keep TalkMuch running smoothly and, with your
                permission, to learn which episodes people love most.
              </p>
            </div>
            <div className="cookie-banner__actions">
              <button
                ref={firstBtnRef}
                type="button"
                className="btn btn--cookie-accept"
                onClick={acceptAll}
              >
                Accept
              </button>
              <button
                type="button"
                className="cookie-banner__manage-link"
                onClick={() => setManaging(true)}
              >
                Manage preferences
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-banner__text">
              <p className="cookie-banner__heading">Manage cookie preferences</p>
            </div>
            <div className="cookie-banner__categories" ref={managePanelRef}>
              {CATEGORIES.map(cat => (
                <label key={cat.id} className="cookie-category">
                  <div className="cookie-category__info">
                    <span className="cookie-category__label">{cat.label}</span>
                    <span className="cookie-category__desc">{cat.description}</span>
                  </div>
                  <input
                    type="checkbox"
                    className="cookie-category__toggle"
                    checked={cat.required ? true : prefs[cat.id]}
                    disabled={cat.required}
                    onChange={() => !cat.required && togglePref(cat.id)}
                    aria-label={`${cat.label} cookies`}
                  />
                </label>
              ))}
            </div>
            <div className="cookie-banner__actions">
              <button
                type="button"
                className="btn btn--sm btn--secondary"
                onClick={() => setManaging(false)}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn--sm btn--primary"
                onClick={saveManaged}
              >
                Save preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
